/* eslint-disable no-underscore-dangle */
import { AccessTime } from "@mui/icons-material";
import PersonIcon from "@mui/icons-material/Person";
import pluralize from "pluralize";
import React from "react";

import { ComputerLabeledIcon, WaitingUserIcon } from "assets/icons";
import { PageContext } from "components/detector/ImageQueryPreview";
import { JANZU_URL } from "config/config";
import { labelInternalToUI, labelMappingsIQ, OTHER_LABEL } from "config/LabelMapping";
import { getCanvasAnnotationsFromROIs } from "features/canvas/utils";
import { DetectorModes } from "types/createDetector";
import { Disagreement } from "types/disagreements";
import { CanvasAnnotation } from "types/imageAnnotations";
import {
  ANSWER_VALUES,
  DisplayBBoxInfo,
  Posicheck,
  ReviewBboxDisplayChoices,
  SOURCE_VALUES,
  type ImageQuery,
} from "types/imageQueries";

import { stringClassNameMap } from "./disagreementsUtils";
import { isExplorePage } from "./utilityFunctions";

type ColorMapping = {
  pattern: RegExp | string;
  color: string;
};

const colorMap: ColorMapping[] = [
  { pattern: "YES", color: "#5E17E6" },
  { pattern: "NO", color: "#CA00EB" },
  { pattern: /^0$|^(>)[1-9]\d*$/, color: "#0450BA" }, // for max_count (EX: >10) and 0 (used for Count)
  { pattern: /^[1-9]\d*$/, color: "#529FE8" }, // This is for numbers (used for Count)
  { pattern: ANSWER_VALUES.__UNSURE, color: "#919599" },
  { pattern: ANSWER_VALUES.UNCLEAR, color: "#919599" },
  { pattern: "HAS_TEXT", color: "#529FE8" },
  { pattern: "NO_TEXT", color: "#0450BA" },
  { pattern: OTHER_LABEL.toUpperCase(), color: "#0450BA" },
  { pattern: "NO OBJECTS", color: "#0450BA" },
  { pattern: "", color: "#0450BA" },
];
const defaultColorState = "#529FE8";

export function getColorMap(value: string): string | undefined {
  return (
    colorMap.find(({ pattern }) => (typeof pattern === "string" ? pattern === value : pattern.test(value)))?.color ||
    defaultColorState
  );
}

// TODO: This function is deprecated. Since we should rely just on IQFTable data we should remove it from the code base.
export function isImageQueryInQueue(imageQuery: ImageQuery | undefined) {
  return imageQuery?.in_queue && !imageQuery?.current_best_answer_source;
}

export const getImageBarTitle = (imageQuery: ImageQuery): string => {
  if (isImageQueryInQueue(imageQuery)) return "Awaiting...";
  if (imageQuery.current_best_answer_str)
    return labelInternalToUI(labelMappingsIQ, imageQuery.current_best_answer_str);
  if (imageQuery.current_best_prediction_str)
    return labelInternalToUI(labelMappingsIQ, imageQuery.current_best_prediction_str);
  return "N/A";
};

// custom overlay bar color for the posicheck image gallery based on the class value
export function getImageBarColor(imageQuery: ImageQuery): string {
  if (imageQuery && imageQuery.iq_id) {
    if (isImageQueryInQueue(imageQuery) || imageQuery.is_placeholder) {
      return "#FFFF";
    }
    const className = imageQuery.current_best_answer_str || imageQuery.current_best_prediction_str;

    // Check if className is not null before indexing colorMap
    return className !== null && className !== undefined // "" is a valid value for className (TEXT_RECOGNITION)
      ? getColorMap(className) || defaultColorState
      : colorMap.find(({ pattern }) => pattern === "UNCLEAR")?.color || defaultColorState;
  }
  return "#919599";
}

// custom overlay bar color for the posicheck image gallery based on the class value
export function getOpacity(imageQuery: ImageQuery | null): string {
  if (imageQuery && imageQuery.iq_id) {
    if (
      imageQuery.current_best_answer_source === SOURCE_VALUES.ALGORITHM ||
      imageQuery.is_placeholder ||
      isImageQueryInQueue(imageQuery)
    ) {
      return `0.75`;
    }
  }
  return "1";
}

export function translateBoundingBoxesLabelToFrontend(label: string) {
  let frontLabel = label;
  if (label === "GREATER_THAN_MAX") {
    frontLabel = "Greater than max";
  }
  if (label === "NO_OBJECTS") {
    frontLabel = "No objects";
  }
  if (label === "BOUNDING_BOX") {
    frontLabel = "Bounding boxes";
  }
  return frontLabel;
}

export function getClassNameAndConfidence(
  imageQuery: ImageQuery,
  pageContext: PageContext
): [className: string, confidence: string] {
  let className = "";
  if (imageQuery.is_placeholder) {
    className = "Awaiting...";
  } else {
    const classname = isExplorePage(pageContext)
      ? imageQuery.current_best_prediction_str
      : imageQuery.current_best_answer_str;

    className = labelInternalToUI(labelMappingsIQ, classname) || "Unclear";
  }

  let confidenceStr = "";
  if (!imageQuery.is_placeholder && className !== "Unclear") {
    const confidence = isExplorePage(pageContext)
      ? imageQuery.current_best_prediction_confidence
      : imageQuery.current_best_answer_confidence;

    confidenceStr = confidence ? `(${confidence.toFixed(2)})` : "";
  }

  className = translateBoundingBoxesLabelToFrontend(className);
  return [className, confidenceStr];
}

export function getColorText(imageQuery: ImageQuery | null): string {
  if (imageQuery && imageQuery.iq_id) {
    return isImageQueryInQueue(imageQuery) || imageQuery.is_placeholder ? "text-gray-700" : "text-white";
  }

  return "text-white";
}

export function getLabeledIcon(imageQuery: ImageQuery) {
  const isInQueue = isImageQueryInQueue(imageQuery);

  if (!imageQuery.iq_id || imageQuery.is_placeholder) {
    return <AccessTime className={`${isInQueue ? "text-gray-700" : "text-white"}`} />;
  }

  if (isImageQueryInQueue(imageQuery)) {
    return <WaitingUserIcon color={isInQueue ? "#919599" : "#ffffff"} />;
  }

  return imageQuery.current_best_answer_source === SOURCE_VALUES.ALGORITHM ? (
    <ComputerLabeledIcon color={isInQueue ? "#919599" : "#ffffff"} />
  ) : (
    <PersonIcon className={`${isInQueue ? "text-gray-700" : "text-white"}`} />
  );
}

export const getTitleSuffix = (
  imageQuery: ImageQuery | undefined,
  detectorMode?: DetectorModes,
  pageContext?: PageContext
) => {
  if (!imageQuery) return undefined;
  if (detectorMode === DetectorModes.COUNT) {
    const value = pageContext
      ? getClassNameAndConfidence(imageQuery, pageContext)[0]
      : imageQuery.current_best_prediction_str;
    if (value === undefined || (value && ["UNCLEAR", "AWAITING..."].includes(value?.toUpperCase()))) return undefined;
    return pluralize(" Object", parseInt(value || "", 10)) || undefined;
  }

  return undefined;
};

interface ConstructImageQueriesUrlOptions {
  type?: "examples" | "answered-v2" | "flagged";
  predictorId?: string;
  pageSize?: number;
  ordering?: string;
  limit?: number;
  answer?: string;
  label?: string;
  explorePage?: boolean;
}

export const constructImageQueriesUrl = ({
  type,
  predictorId,
  pageSize,
  ordering,
  limit,
  answer,
  explorePage,
  label,
}: ConstructImageQueriesUrlOptions) => {
  const searchParams = new URLSearchParams();

  if (explorePage) {
    searchParams.append('detector_id', predictorId || '');
    searchParams.append('page_size', '12');
    return `${JANZU_URL}reef-api/image-queries?${searchParams.toString()}`;
  }

  searchParams.append('predictor_id', predictorId || '');

  if (pageSize !== undefined) {
    searchParams.append('page_size', pageSize.toString());
  }

  if (ordering !== undefined) {
    searchParams.append('ordering', ordering);
  }

  if (limit !== undefined) {
    searchParams.append('limit', limit.toString());
  }

  if (answer !== undefined) {
    searchParams.append('answer', answer);
  }

  if (label !== undefined) {
    searchParams.append('label', label.toUpperCase());
  }

  return `${JANZU_URL}reef-api/image-queries/${type}?${searchParams.toString()}`;
};

/**
 * Function helper to build an image query from the data of a posicheck.
 *
 * The Posicheck type is being deprecated in favor of ImageQuery type, so this
 * function helps with the forwards compatibility.
 */
export function getImageQueryFromPosicheck(posicheck: Posicheck): ImageQuery {
  return {
    query: posicheck.predictor.label_prompt,
    predictor_id: posicheck.predictor.id,
    iq_id: posicheck.id,
    image_id: posicheck.image_id,
    best_prediction_label_id: posicheck.best_prediction?.id ?? null,
    current_best_prediction_str: posicheck.best_prediction
      ? stringClassNameMap[posicheck.best_prediction.class_name]
      : null,
    current_best_prediction_rois: posicheck.best_prediction?.rois
      ? posicheck.best_prediction.rois.map((roi) => ({
          cls: "ROI",
          init_args: {
            ...roi,
            version: "2.0",
            geometry: {
              cls: "BBoxGeometry",
              init_args: {
                ...roi.geometry,
                version: "2.0",
              },
            },
          },
        }))
      : null,
    current_best_prediction_source: (posicheck.best_prediction?.source ?? null) as SOURCE_VALUES | null,
    current_best_prediction_confidence: posicheck.best_prediction?.confidence ?? null,
    patience_time: posicheck.patience_time,
    created_at: posicheck.created_at,
    human_labeled: posicheck.human_review_label !== null,
    confidence_threshold: posicheck.confidence_threshold,
    predictor_name: posicheck.predictor.name,
    latest_flagged_label_feedback_text: null,
    flag_archived: posicheck.best_prediction?.flag_archived ?? false,
    is_placeholder: false,
    in_queue: false,
    is_audited: false,
    ground_truth_str: null,
    ground_truth_source: null,
    ground_truth_confidence: null,
    ml_answer_str: null,
    ml_answer_confidence: null,
    ml_prediction_str: null,
    ml_prediction_confidence: null,
    current_best_answer_str: null,
    current_best_answer_source: null,
    current_best_answer_confidence: null,
    system_answer_str: null,
    system_answer_source: null,
    system_answer_confidence: null,
    system_prediction_str: null,
    system_prediction_source: null,
    system_prediction_confidence: null,
  };
}

/**
 * Function helper to build an image query from the data of a disagreement.
 */
export function getImageQueryFromDisagreement(disagreement: Disagreement): ImageQuery {
  return {
    query: disagreement.predictor.label_prompt,
    predictor_id: disagreement.predictor.id,
    iq_id: disagreement.iq_id,
    image_id: "",
    best_prediction_label_id: disagreement.challenged_answer.id,
    current_best_prediction_str: stringClassNameMap[disagreement.challenged_answer.class_name],
    current_best_prediction_rois: disagreement.challenged_answer.rois,
    current_best_prediction_source: disagreement.challenged_answer.source,
    current_best_prediction_confidence: disagreement.challenged_answer.confidence,
    patience_time: disagreement.predictor.patience_time,
    created_at: "",
    human_labeled: false,
    confidence_threshold: disagreement.predictor.confidence_threshold,
    predictor_name: disagreement.predictor.name,
    latest_flagged_label_feedback_text: null,
    flag_archived: false,
    is_placeholder: false,
    in_queue: false,
    is_audited: false,
    ground_truth_str: null,
    ground_truth_source: null,
    ground_truth_confidence: null,
    ml_answer_str: null,
    ml_answer_confidence: null,
    ml_prediction_str: null,
    ml_prediction_confidence: null,
    current_best_answer_str: null,
    current_best_answer_source: null,
    current_best_answer_confidence: null,
    system_answer_str: null,
    system_answer_source: null,
    system_answer_confidence: null,
    system_prediction_str: null,
    system_prediction_source: null,
    system_prediction_confidence: null,
  };
}

export function getCanvasAnnotationsFromReviewBBoxInfo(reviewBBoxInfo: DisplayBBoxInfo): CanvasAnnotation[] {
  switch (reviewBBoxInfo.display_bbox) {
    case ReviewBboxDisplayChoices.ML_BOXES:
      return reviewBBoxInfo.display_bbox_rois ? getCanvasAnnotationsFromROIs(reviewBBoxInfo.display_bbox_rois) : [];

    case ReviewBboxDisplayChoices.NO_BOXES:
      return [];

    default:
      return [];
  }
}
